@file:Suppress("FunctionName")

package components

import Dialog
import kotlinx.browser.document
import kotlinx.coroutines.launch
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.*
import mainScope
import onSubmitSuspend
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import query

private var nextActionButtonId: Int = 0

fun GenericDialog(
    title: String,
    message: String,
    acknowledgeText: String,
) = document.create.div("generic-dialog") {
    h2 {
        + title
    }

    div("dialog-message") {
        + message
    }

    div("space-between") {
        button(classes = "primary") {
            + acknowledgeText

            onClickFunction = {
                Dialog.remove()
            }
        }
    }
}

fun GenericActionDialog(
    title: String? = null,
    message: String?,
    acceptText: String?,
    rejectText: String?,
    destructive: Boolean = false,
    doubleActionMode: Boolean = false,
    onAccept: suspend () -> Unit,
    onReject: (() -> Unit)? = null,
) = document.create.div("generic-dialog") {
    if (title != null) {
        h2 {
            +title
        }
    }

    if (message != null) {
        div("dialog-message pre-line") {
            +message
        }
    }

    val buttonSpacing = if (acceptText == null || rejectText == null) "space-around" else "space-between"
    div(buttonSpacing) {
        if (rejectText != null) {
            val classes = if (doubleActionMode) "primary" else "flat"
            button(classes = classes) {
                +rejectText

                onClickFunction = {
                    onReject?.invoke()
                    Dialog.remove()
                }
            }
        }

        if (acceptText != null) {
            val actionId = "generic-action-button-${++nextActionButtonId}"
            val classes = if (destructive) "danger" else "primary"

            ActionButton(id = actionId, text = acceptText, classes = classes) {
                // Remove dialog before accepting, in case the onAccept() opens its own dialog. Gets weird otherwise.
                Dialog.remove()
                try {
                    onAccept()
                } catch (_: Exception) {
                    return@ActionButton
                }
            }
        }
    }
}

fun InputDialog(
    message: String,
    acceptText: String,
    placeholderText: String? = null,
    onAccept: ((String) -> Unit)? = null,
    onAcceptSuspend: (suspend (String) -> Unit)? = null,
) = document.create.div("input-dialog") {
    h4("dialog-message") {
        + message
    }

    var inputValue = ""

    form {
        onSubmitFunction = { event ->
            event.preventDefault()
            if (onAccept != null) {
                onAccept(inputValue)
                Dialog.remove()
            }
            if (onAcceptSuspend != null) {
                mainScope.launch {
                    actionButtonChangeState("generic-input-dialog-action", isUpdating = true)
                    onAcceptSuspend(inputValue)
                    actionButtonChangeState("generic-input-dialog-action", isUpdating = false)
                    Dialog.remove()
                }
            }
        }

        div {
            input {
                placeholderText?.let { placeholder = it }

                onInputFunction = {
                    val input = it.currentTarget as HTMLInputElement
                    inputValue = input.value
                }
            }
        }

        div("mt-12") {
            ActionButton(
                id = "generic-input-dialog-action",
                text = acceptText,
                type = ButtonType.submit,
                actionType = ActionButtonType.BENIGN,
            )
        }
    }
}

fun InputActionDialog(
    message: String,
    defaultValue: String = "",
    acceptText: String = "Save",
    buttonClasses: String = "primary",
    onAccept: suspend (value: String) -> Unit,
) = document.create.div("input-dialog") {
    h4("dialog-message") {
        + message
    }

    val actionId = "generic-action-button-${++nextActionButtonId}"

    form {
        onSubmitSuspend = submit@{ event ->
            val input = (event.currentTarget as HTMLElement).query<HTMLInputElement>("input")

            actionButtonChangeState(actionId, isUpdating = true)

            try {
                onAccept(input.value)
            } catch (e: Exception) {
                actionButtonChangeState(actionId, isUpdating = false)
                return@submit
            }
            Dialog.remove()
        }

        div {
            input {
                value = defaultValue
            }
        }

        div("mt-12") {
            ActionButton(id = actionId, text = acceptText, classes = buttonClasses)
        }
    }
}

fun SelectDialog(
    message: String,
    acceptText: String,
    options: List<Pair<String, String>>,
    defaultValue: String,
    label: String,
    onAccept: (String) -> Unit,
) = document.create.div("select-dialog") {
    h4("dialog-message") {
        + message
    }

    var inputValue = options.first { it.second == defaultValue }.second

    form {
        onSubmitFunction = { event ->
            event.preventDefault()

            onAccept(inputValue)
            Dialog.remove()
        }

        div {
            label {
                span("mr-12") { + label }

                select {
                    options.forEach { (displayName, value) ->
                        option {
                            +displayName

                            this.value = value

                            selected = value == defaultValue
                        }
                    }

                    onChangeFunction = { event ->
                        val select = event.currentTarget as HTMLSelectElement
                        inputValue = select.value
                    }
                }
            }
        }

        div("mt-12") {
            button(type = ButtonType.submit, classes = "primary") {
                +acceptText
            }
        }
    }
}
